import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/App';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { FileText, AlertCircle, Download, Eye, TrendingUp, TrendingDown, FileSpreadsheet, FileBox, Calendar, CheckCircle2, ChevronDown, ChevronRight, Sparkles, Loader2, Activity, Table2, Receipt, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import LedgersTab from '../components/LedgersTab';
import FinancialInsightsTab from '../components/FinancialInsightsTab';
import SmartCenterView from '../components/SmartCenterView';
import CenterHealth from './CenterHealth';
import ExpenseIntelligenceTab from '../components/ExpenseIntelligenceTab';
import SalesGridEditor from '../components/SalesGridEditor';
import ExpenseEntry from '../components/ExpenseEntry';
import { useCurrency } from '../hooks/useCurrency';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import SalaryManpowerCostSection from './SalaryManpowerCostSection';
import { InvestmentReturnProjection } from '../components/InvestmentReturnProjection';
import { Target } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Small wrapper that turns a Card section into a click-to-collapse panel.
// Header stays clickable; chevron flips; content animates with the browser's
// native height-auto rules (no extra deps).
function CollapsibleSection({ id, title, description, defaultOpen = true, className = '', children, testId, headerExtra, open: openProp, onOpenChange }) {
  const [openLocal, setOpenLocal] = useState(defaultOpen);
  const open = openProp ?? openLocal;
  const setOpen = (fn) => { const v = typeof fn === 'function' ? fn(open) : fn; setOpenLocal(v); onOpenChange?.(v); };
  return (
    <Card data-testid={testId || id} className={className}>
      <CardHeader
        className="pb-3 cursor-pointer select-none"
        onClick={() => setOpen(v => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(v => !v); } }}
        data-testid={`${testId || id}-toggle`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
            <div className="min-w-0">
              <CardTitle className="text-base">{title}</CardTitle>
              {description && <CardDescription className="mt-0.5">{description}</CardDescription>}
            </div>
          </div>
          {headerExtra}
        </div>
      </CardHeader>
      {open && <CardContent>{children}</CardContent>}
    </Card>
  );
}

const MONTHS = [
  '01', '02', '03', '04', '05', '06',
  '07', '08', '09', '10', '11', '12'
];
const YEARS = (() => {
  const y = new Date().getFullYear();
  const out = [];
  for (let i = y; i >= 2023; i--) out.push(String(i));
  return out;
})();

export default function OwnerReports() {
  const { session } = useAuth();
  const [centers, setCenters] = useState([]);
  const [center, setCenter] = useState('');
  const currency = useCurrency(center);
  // Component-scope formatter — automatically switches symbol/locale by center.
  const fmtMoney = (v) => currency.format(v);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [downloadingAll, setDownloadingAll] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  // Sales/Expense Excel filter state
  const [seMode, setSeMode] = useState('month'); // 'month' | 'range' | 'date'
  const [seStart, setSeStart] = useState('');
  const [seEnd, setSeEnd] = useState('');
  // Controlled tab state so Smart Center View can jump back to Monthly + scroll
  const [activeTab, setActiveTab] = useState('monthly');
  // Controlled inner sub-tab for the Monthly Report tab. openMonthlySection()
  // routes deep-links from Smart Center View to the correct sub-tab so the
  // scroll target is guaranteed to be mounted.
  const [monthlyInnerTab, setMonthlyInnerTab] = useState('overview');
  const [gridOpen, setGridOpen] = useState({ sales: false, expenses: false });
  const reportReq = useRef(0);
  const openGrid = (key) => {
    setGridOpen((g) => ({ ...g, [key]: true }));
    setTimeout(() => document.querySelector(`[data-testid="${key === 'sales' ? 'or-sales-grid-view' : 'or-expense-grid-view'}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };
  // Handler passed to SmartCenterView — switches to Monthly tab and scrolls to a section
  const openMonthlySection = useCallback((section) => {
    setActiveTab('monthly');
    // Route to correct inner sub-tab (Overview / Reports / Ledgers) BEFORE
    // scrolling, otherwise the target testid isn't mounted yet.
    const subTabMap = {
      sales: 'overview',
      expenses: 'overview',
      'financial-insights': 'overview',
      commissions: 'reports',
      gst: 'reports',
      'owner-ledger': 'reports',
      downloads: 'reports',
      bundle: 'reports',
      ledgers: 'ledgers',
    };
    setMonthlyInnerTab(subTabMap[section] || 'reports');
    // wait for tab render, then scroll to matching section
    setTimeout(() => {
      const sectionMap = {
        sales: 'or-sales-grid-view',
        expenses: 'or-expense-grid-view',
        'financial-insights': 'or-financial-insights-overview',
        commissions: 'or-downloads',   // platform commissions live in bundle downloads now
        gst: 'or-downloads',            // GST PDF lives inside downloads card
        'owner-ledger': 'or-owner-ledger-card',
        downloads: 'or-downloads',
        bundle: 'or-download-bundle-card',
        ledgers: 'or-monthly-ledgers-content',
      };
      const target = sectionMap[section] || sectionMap.downloads;
      const el = document.querySelector(`[data-testid="${target}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  }, []);
  // Raw uploaded files for the selected month
  const [rawFiles, setRawFiles] = useState([]);
  const [rawFilesLoading, setRawFilesLoading] = useState(false);

  // Fetch the Sales/Expense Excel with the chosen filter
  const fetchSalesExpenseExcel = async (mode) => {
    try {
      const body = { token: session.token, center, mode: seMode };
      if (seMode === 'month') body.month = `${year}-${month}`;
      else if (seMode === 'range') { body.start_date = seStart; body.end_date = seEnd; }
      else if (seMode === 'date')  { body.start_date = seStart; }
      const res = await fetch(`${API}/api/franchise-reports/sales-expense-excel`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.detail || 'Failed'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (mode === 'preview') {
        // Excel can't render inline in <iframe>; show a sheet-style preview using a
        // SheetJS-driven view would be ideal, but for now offer Download with toast.
        const a = document.createElement('a');
        a.href = url; a.download = `Sales_Expense_${center}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.info('Excel cannot be previewed inline — downloaded instead');
      } else {
        const a = document.createElement('a');
        a.href = url; a.download = `Sales_Expense_${center}_${seMode === 'month' ? `${year}-${month}` : `${seStart}_${seEnd || seStart}`}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success('Sales/Expense Excel downloaded');
      }
    } catch (e) { toast.error(e.message); }
  };

  // Fetch the Franchise Owner Ledger PDF (kept available outside the Email Pack)
  const fetchOwnerLedgerPdf = async (mode, lmode) => {
    try {
      const body = { token: session.token, center, period_type: 'month', month: `${year}-${month}`, fmt: 'pdf' };
      if (lmode === 'range') { body.period_type = 'range'; body.from_month = `${year}-${month}`; body.to_month = `${year}-${month}`; }
      const res = await fetch(`${API}/api/ledgers/owner`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.detail || 'Failed'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (mode === 'preview') {
        setPreviewBlobUrl(url);
        setPreviewTitle(`Franchise Owner Ledger — ${center} · ${year}-${month}`);
      } else {
        const a = document.createElement('a');
        a.href = url; a.download = `Owner_Ledger_${center}_${year}-${month}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success('Owner Ledger downloaded');
      }
    } catch (e) { toast.error(e.message); }
  };

  // Load raw uploaded files for the current center+month
  const loadRawFiles = useCallback(async () => {
    if (!center) return;
    setRawFilesLoading(true);
    try {
      const res = await fetch(`${API}/api/franchise-reports/raw-files`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.token, center, month: `${year}-${month}` }),
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.detail || 'Failed'); }
      const data = await res.json();
      setRawFiles(data.files || []);
    } catch (e) {
      toast.error(e.message); setRawFiles([]);
    } finally {
      setRawFilesLoading(false);
    }
  }, [session?.token, center, year, month]);

  // Download a single raw file
  const downloadRawFile = async (rawId, filename) => {
    try {
      const res = await fetch(`${API}/api/franchise-reports/raw-file/download`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.token, raw_id: rawId }),
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.detail || 'Failed'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'raw_file';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  // Common helper — fetch a PDF and either trigger download or show preview modal.
  const fetchReportPdf = async (r, mode) => {
    try {
      const res = await fetch(`${API}/api/${r.endpoint || 'center-accounts'}/${r.path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.token, center, month: `${year}-${month}` }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Failed' }));
        throw new Error(err.detail || 'Failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (mode === 'preview') {
        setPreviewBlobUrl(url);
        setPreviewTitle(`${r.label} — ${center} · ${year}-${month}`);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${r.label.replace(/\s+/g, '_')}_${center}_${year}-${month}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(`${r.label} downloaded`);
      }
    } catch (e) { toast.error(e.message); }
  };

  const fetchMgPdf = async (mode) => {
    try {
      const res = await fetch(`${API}/api/center-accounts/export-mg-payout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: session.token, center,
          from_month: `${year}-${month}`, to_month: `${year}-${month}`,
          format: 'pdf',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Failed' }));
        throw new Error(err.detail || 'Failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (mode === 'preview') {
        setPreviewBlobUrl(url);
        setPreviewTitle(`MG Report — ${center} · ${year}-${month}`);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `MG_Report_${center}_${year}-${month}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success('MG Report downloaded');
      }
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    // Staff (Admin / Super Admin / Accountant) can pick any center — fetch
    // the full centers list. Franchise Owners are scoped to ownerCenters
    // returned by login.
    const isStaff = !!(session?.is_super_admin || session?.is_admin
      || session?.role_key === 'accountant' || session?.roles?.accounting);
    (async () => {
      if (isStaff) {
        try {
          const res = await fetch(`${API}/api/mgt/centers`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: session.token }),
          });
          const data = await res.json();
          const list = (data.centers || []).map(c => c.code || c).filter(Boolean);
          if (list.length) {
            setCenters(list);
            setCenter(prev => prev || list[0]);
            return;
          }
        } catch { /* fall through */ }
      }
      if (session?.ownerCenters?.length) {
        setCenters(session.ownerCenters);
        setCenter(session.ownerCenters[0]);
      } else if (session?.center) {
        setCenters([session.center]);
        setCenter(session.center);
      }
    })();
  }, [session]);

  const load = useCallback(async () => {
    if (!center) return;
    const reqId = ++reportReq.current;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/owner-reports/monthly-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.token, center, month: `${year}-${month}` }),
      });
      const data = await res.json();
      if (reqId !== reportReq.current) return;
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setReport(data);
    } catch (e) {
      if (reqId !== reportReq.current) return;
      toast.error(e.message);
      setReport(null);
    } finally {
      if (reqId === reportReq.current) setLoading(false);
    }
  }, [center, year, month, session]);

  useEffect(() => { if (center) load(); }, [center, year, month, load]);
  useEffect(() => { if (center) loadRawFiles(); }, [center, year, month, loadRawFiles]);

  const visible = report?.visibility?.ready;
  const notReadyReason = report?.visibility?.reason || 'Current month in progress. Accounts team has not yet approved visibility.';
  // Staff (Super Admin / Admin / Accounts) can always download / view reports
  // regardless of whether the month has been released to franchise owners.
  // Franchise owners only get downloads after release. This mirrors the
  // server-side gate at /api/center-accounts/generate-* and /api/ledgers/*.
  const canBypassRelease = !!(session?.is_super_admin || session?.is_admin
    || session?.role_key === 'accountant' || session?.roles?.accounting);
  const canDownload = visible || canBypassRelease;

  return (
    <div className="max-w-[1640px] mx-auto p-4 sm:p-6 space-y-6" data-testid="owner-reports-page">
      {/* Phase-3 deprecation banner — these standalone PDFs are being
          consolidated into the 3 master bundles. The page still works
          (PIB / GST / Sales / Expenses email pack), but new consumers
          should download the persona-specific bundle instead. */}
      <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm" data-testid="owner-reports-deprecation-banner">
        <p className="font-medium text-amber-900 dark:text-amber-100">
          ℹ️ This page is moving.
        </p>
        <p className="text-amber-800 dark:text-amber-200 mt-1">
          The standalone PIB / Email Pack PDFs are being consolidated into the new <b>3-bundle architecture</b>.
          For Franchise Owners → use the new <a href="/dashboard/franchise-owner" className="underline font-medium">Franchise Owner Dashboard</a> →
          <b> Bundle</b> button. For Accounts Team → <a href="/dashboard/ca" className="underline font-medium">CA Dashboard</a>.
          For Founders → <a href="/dashboard/franchisor" className="underline font-medium">Franchisor Dashboard</a>.
          This page will remain available for historical generation but will not receive new features.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex flex-wrap h-auto gap-1 mb-4 p-1 justify-start">
          <TabsTrigger value="monthly" data-testid="or-tab-monthly">
            <FileText className="w-4 h-4 mr-2" /> Monthly Reports
          </TabsTrigger>
          <TabsTrigger value="smart" data-testid="or-tab-smart">
            <Sparkles className="w-4 h-4 mr-2" /> Smart Center View
          </TabsTrigger>
          <TabsTrigger value="health" data-testid="or-tab-health">
            <Activity className="w-4 h-4 mr-2" /> Profitability Health
          </TabsTrigger>
          <TabsTrigger value="investment-projection" data-testid="or-tab-investment-projection" className="text-emerald-800 font-semibold">
            <Target className="w-4 h-4 mr-2" /> Investment Projection
          </TabsTrigger>
          <TabsTrigger value="expense-intel" data-testid="or-tab-expense-intel" className="text-purple-700">
            <Sparkles className="w-4 h-4 mr-2" /> Expense Intelligence
          </TabsTrigger>
          <TabsTrigger value="salary" data-testid="or-tab-salary" className="text-emerald-700">
            <FileText className="w-4 h-4 mr-2" /> Salary &amp; Manpower
          </TabsTrigger>
        </TabsList>

        <TabsContent value="salary" className="space-y-6">
          <SalaryManpowerCostSection session={session} defaultMonth={`${year}-${month}`} />
        </TabsContent>

        <TabsContent value="investment-projection" className="space-y-6" data-testid="or-investment-projection-content">
          <InvestmentReturnProjection center={center} readOnly={true} />
        </TabsContent>

        <TabsContent value="monthly" className="space-y-6">
        {/* Top-level batch download toolbar was moved into the Reports sub-tab
            below, since sections 5 and 6 of the tab restructure specify that
            downloads live under Reports. */}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3 text-[#750817]"><span className="grid h-12 w-12 place-items-center rounded-2xl border-2 border-[#d9ae52] bg-[#750817] text-[#F4D58D]"><FileText className="w-7 h-7" /></span> Owner Reports <span className="text-xs font-normal text-muted-foreground">(Monthly · legacy)</span></h1>
          <p className="text-base text-[#5a3a22] mt-1">Business performance, financial insights and reports for the selected centre — PIB · GST · Sales · Expenses.</p>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Filters</CardTitle>
          {(session?.is_super_admin || session?.is_admin || session?.role_key === 'accountant' || session?.roles?.accounting) && (
            <div className="flex gap-2">
              <Button
                size="sm"
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="or-release-all-btn"
                onClick={async () => {
                  if (!window.confirm(`Release ${year}-${month} for ALL active centers to franchise owners?`)) return;
                  try {
                    const res = await fetch(`${API}/api/owner-reports/release-all`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ token: session.token, month: `${year}-${month}`, ready: true }),
                    });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.detail || 'Failed');
                    toast.success(`Released ${year}-${month} for ${data.updated} center(s).`);
                    load();
                  } catch (e) { toast.error(e.message); }
                }}
              >Release All for {year}-{month}</Button>
              <Button
                size="sm"
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="or-revoke-all-btn"
                onClick={async () => {
                  if (!window.confirm(`Revoke owner access to ${year}-${month} for ALL centers?`)) return;
                  try {
                    const res = await fetch(`${API}/api/owner-reports/release-all`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ token: session.token, month: `${year}-${month}`, ready: false }),
                    });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.detail || 'Failed');
                    toast.success(`Revoked ${year}-${month} across ${data.updated} center(s).`);
                    load();
                  } catch (e) { toast.error(e.message); }
                }}
              >Revoke All</Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Center</label>
              <Select value={center} onValueChange={setCenter}>
                <SelectTrigger data-testid="or-center-select"><SelectValue placeholder="Select center" /></SelectTrigger>
                <SelectContent>
                  {centers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Year</label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger data-testid="or-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Month</label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger data-testid="or-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map(m => (
                    <SelectItem key={m} value={m}>
                      {new Date(`2024-${m}-01`).toLocaleDateString('en-IN', { month: 'long' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={load} disabled={loading || !center} className="w-full bg-[#8B0000] hover:bg-[#6B0000]" data-testid="or-load-btn">
                {loading ? 'Loading…' : 'Load Report'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Visibility gate — only block franchise owners with no release. Staff
          see the admin_bypass banner instead and full data + downloads. */}
      {report && !visible && !canBypassRelease && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-900/10" data-testid="or-gated-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900 dark:text-amber-100">Report not yet available</p>
              <p className="text-sm text-amber-800 dark:text-amber-200">{notReadyReason}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin bypass notice — data is shown but owners still blocked */}
      {report?.visibility?.admin_bypass && (
        <Card className="border-sky-300 bg-sky-50 dark:bg-sky-900/10" data-testid="or-admin-bypass-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-sky-700 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-sky-900 dark:text-sky-100">Admin preview — not yet released to owners</p>
              <p className="text-sm text-sky-800 dark:text-sky-200">
                Accounts team has not flagged this month as ready. Franchise owners will see the &quot;Report not yet available&quot; screen until released.
              </p>
            </div>
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="or-release-btn"
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/api/owner-reports/set-visibility`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      token: session.token, center, month: `${year}-${month}`, ready: true,
                      note: `Released by ${session.managerName || 'admin'} on ${new Date().toLocaleString()}`,
                    }),
                  });
                  const data = await res.json();
                  if (!res.ok) throw new Error(data.detail || 'Failed');
                  toast.success(`Released ${center} · ${year}-${month} to franchise owners.`);
                  load();
                } catch (e) { toast.error(e.message); }
              }}
            >Release to Owners</Button>
          </CardContent>
        </Card>
      )}

      {/* Released banner — admin can revoke */}
      {report?.visibility?.flagged_ready && (
        <Card className="!border-2 !border-emerald-300 bg-emerald-50 dark:bg-emerald-900/10" data-testid="or-released-banner">
          <CardContent className="p-4 flex items-center gap-3 flex-wrap">
            <CheckCircle2 className="w-7 h-7 text-emerald-700" />
            <div className="flex-1">
              <p className="font-semibold text-emerald-900 dark:text-emerald-100">Released to franchise owners ✓</p>
              <p className="text-sm text-emerald-800 dark:text-emerald-200">
                {report.visibility.note || 'This month is visible to franchise owners.'}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="or-revoke-btn"
              onClick={async () => {
                if (!window.confirm('Revoke access so franchise owners can no longer see this month?')) return;
                try {
                  const res = await fetch(`${API}/api/owner-reports/set-visibility`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      token: session.token, center, month: `${year}-${month}`, ready: false,
                      note: `Revoked by ${session.managerName || 'admin'} on ${new Date().toLocaleString()}`,
                    }),
                  });
                  const data = await res.json();
                  if (!res.ok) throw new Error(data.detail || 'Failed');
                  toast.success(`Access revoked for ${center} · ${year}-${month}.`);
                  load();
                } catch (e) { toast.error(e.message); }
              }}
            >Revoke Access</Button>
          </CardContent>
        </Card>
      )}

      {report && visible && !canBypassRelease && null}

      {/* ══ Inner sub-tabs for Monthly Report ═══════════════════════════════
          Overview → KPIs + Financial Insights + Sales Grid + Expense Grid
          Reports  → Bundle ZIP + individual PDFs + Sales/Expense Excel + Raw Files
          Ledgers  → HQ ↔ Franchise running books (same as Center Accounts) */}
      {center && (
        <Tabs value={monthlyInnerTab} onValueChange={setMonthlyInnerTab} className="w-full">
          <TabsList className="grid w-full max-w-xl grid-cols-3 mb-4 !min-h-[3.25rem] [&>button]:text-base [&>button]:py-2.5" data-testid="or-monthly-inner-tabs">
            <TabsTrigger value="overview" data-testid="or-monthly-overview">
              <TrendingUp className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="or-monthly-reports-tab">
              <FileText className="w-4 h-4 mr-2" /> Reports
            </TabsTrigger>
            <TabsTrigger value="ledgers" data-testid="or-monthly-ledgers-tab">
              <BookOpen className="w-4 h-4 mr-2" /> Ledgers
            </TabsTrigger>
          </TabsList>

          {/* ── OVERVIEW SUB-TAB — sections 1-4 in the user-approved order ─── */}
          <TabsContent value="overview" className="space-y-6" data-testid="or-monthly-overview-content">
            {/* Section 2: Financial Insights (view-only) */}
            {center && (canDownload || canBypassRelease) && (
              <div data-testid="or-financial-insights-overview">
                <FinancialInsightsTab
                  centersList={[{ code: center, name: center }]}
                  centerOverride={center}
                  monthOverride={`${year}-${month}`}
                  owner={{
                    report: report && canDownload && !loading ? report : null,
                    fmt: fmtMoney, center, month: `${year}-${month}`,
                    onMonthChange: (m) => { const [y, mm] = m.split('-'); setYear(y); setMonth(mm); },
                    onKpi: openGrid,
                  }}
                />
              </div>
            )}

            {center && (canDownload || canBypassRelease) && (
              <div className="space-y-4" data-testid="or-summary-strips">
                <CenterHealth embedded strip externalCenter={center} externalMonth={`${year}-${month}`} />
                <InvestmentReturnProjection center={center} readOnly={true} strip />
                <ExpenseIntelligenceTab
                  session={session}
                  center={center}
                  month={`${year}-${month}`}
                  currency={String(center || '').toUpperCase().endsWith('-PERTH') ? 'AUD' : 'INR'}
                  editable={false}
                  simplified={true}
                  storageKey="owner-ei-strip"
                  strip
                />
              </div>
            )}
            {center && (canDownload || canBypassRelease) && (
              <h2 className="text-2xl font-bold text-[#750817] flex items-center gap-2 pt-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="or-detailed-grids-title">
                <Table2 className="w-6 h-6" /> Detailed Grids</h2>
            )}
            {/* Section 3: Sales Grid — same as Sales Dashboard, view-only */}
            {center && (canDownload || canBypassRelease) && (
              <CollapsibleSection
                open={gridOpen.sales}
                onOpenChange={(v) => setGridOpen((g) => ({ ...g, sales: v }))}
                id="or-sales-grid-view"
                testId="or-sales-grid-view"
                title={<span className="flex items-center gap-2 text-lg"><Table2 className="w-5 h-5 text-[#750817]" /> Sales Grid (View-Only)</span>}
                description="Same live sales grid available on the Sales Dashboard — no add/edit/delete."
                defaultOpen={false}
              >
                <SalesGridEditor
                  session={session}
                  selectedCenter={center}
                  selectedMonth={`${year}-${month}`}
                  centersList={centers.map(c => ({ code: c }))}
                  readOnly={true}
                />
              </CollapsibleSection>
            )}

            {/* Section 4: Expense Grid — same as Sales Dashboard, view-only */}
            {center && (canDownload || canBypassRelease) && (
              <CollapsibleSection
                open={gridOpen.expenses}
                onOpenChange={(v) => setGridOpen((g) => ({ ...g, expenses: v }))}
                id="or-expense-grid-view"
                testId="or-expense-grid-view"
                title={<span className="flex items-center gap-2 text-lg"><Receipt className="w-5 h-5 text-[#750817]" /> Expense Grid (View-Only)</span>}
                description="Same live expense entries as the Sales Dashboard — no add / edit / import / delete."
                defaultOpen={false}
              >
                <ExpenseEntry
                  session={session}
                  monthRange={`${year}-${month}`}
                  selectedCenter={center}
                  centersList={centers.map(c => ({ code: c }))}
                  readOnly={true}
                />
              </CollapsibleSection>
            )}
          </TabsContent>

          {/* ── REPORTS SUB-TAB — all downloadable reports ───────────────── */}
          <TabsContent value="reports" className="space-y-6" data-testid="or-monthly-reports-content">
            {/* Top-level batch download buttons — moved here from the top of the tab */}
            <div className="flex flex-wrap items-center justify-end gap-2" data-testid="or-download-all-toolbar-reports">
              <Button
                size="sm" variant="outline"
                data-testid="or-download-all-pdf-btn-reports"
                onClick={async () => {
                  setDownloadingAll('pdf');
                  let ok = 0;
                  try {
                    for (const kind of ['pib', 'gst', 'sales', 'expenses']) {
                      try {
                        const res = await fetch(`${API}/api/franchise-reports/bundle`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ token: session.token, center, month: `${year}-${month}`, kind, format: 'pdf' }),
                        });
                        if (!res.ok) continue;
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = `${center}_${year}-${month}_${kind}.pdf`;
                        document.body.appendChild(a); a.click(); a.remove();
                        URL.revokeObjectURL(url);
                        ok += 1;
                      } catch { /* skip if endpoint missing */ }
                    }
                    if (ok > 0) toast.success(`Downloaded ${ok} of 4 bundle PDFs`);
                    else toast.error('No bundle PDFs were available for this month');
                  } finally { setDownloadingAll(null); }
                }}
                disabled={!!downloadingAll}
              >
                {downloadingAll === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                Download All PDF
              </Button>
              <Button
                size="sm" variant="outline"
                data-testid="or-download-all-excel-btn-reports"
                onClick={async () => {
                  setDownloadingAll('excel');
                  try {
                    const res = await fetch(`${API}/api/franchise-reports/sales-expense-excel`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ token: session.token, center, month: `${year}-${month}` }),
                    });
                    if (!res.ok) throw new Error('Failed');
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = `${center}_${year}-${month}_all.xlsx`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                    toast.success('Download All Excel started');
                  } catch (e) { toast.error(e.message); }
                  finally { setDownloadingAll(null); }
                }}
                disabled={!!downloadingAll}
              >
                {downloadingAll === 'excel' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}
                Download All Excel
              </Button>
            </div>

      {report && canDownload && (
        <>
          {/* Hero: Download Complete Monthly Bundle */}
          <Card data-testid="or-download-bundle-card" className="border-2 border-rose-300 bg-rose-50/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0">
                    <Download className="w-7 h-7 text-rose-700" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-rose-900">Download Complete Monthly Bundle</h4>
                    <p className="text-sm text-rose-700 mt-1 max-w-xl">
                      One ZIP with everything for {center} · {year}-{month} — PIB, GST, Commission, Bank Statement PDFs + Sales/Expense Excel + Franchise Owner Ledger + any raw uploaded files (Swiggy / Zomato / Bank).
                    </p>
                  </div>
                </div>
                <Button
                  size="lg"
                  className="bg-rose-700 hover:bg-rose-800 text-white"
                  data-testid="or-download-bundle-btn"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API}/api/franchise-reports/bundle`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ token: session.token, center, month: `${year}-${month}` }),
                      });
                      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed'); }
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = `Monthly_Bundle_${center}_${year}-${month}.zip`;
                      document.body.appendChild(a); a.click(); a.remove();
                      URL.revokeObjectURL(url);
                      toast.success('Monthly bundle downloaded');
                    } catch (e) { toast.error(e.message); }
                  }}
                >
                  <Download className="w-4 h-4 mr-2" /> Download Bundle (ZIP)
                </Button>
              </div>
              <p className="text-xs text-rose-700 mt-3">Prefer to preview each report first? Open the collapsible sections below.</p>
            </CardContent>
          </Card>

          {/* Download Reports — PDFs */}
          <CollapsibleSection
            id="or-downloads"
            testId="or-downloads"
            title={<span className="flex items-center gap-2"><Download className="w-4 h-4" /> Download Reports (PDF)</span>}
            description={<>Pre-formatted statements for {center} · {year}-{month}. Click <strong>Preview</strong> to view inline before downloading.</>}
            defaultOpen={true}
          >
            <div className="flex flex-wrap gap-3">
                {[
                  { id: 'pib', label: 'PIB Report', path: 'generate-pib', endpoint: 'center-accounts', color: 'bg-[#8B0000] hover:bg-[#6B0000] text-white' },
                  { id: 'gst', label: 'GST Summary', path: 'generate-gst-summary', endpoint: 'center-accounts', color: 'bg-amber-600 hover:bg-amber-700 text-white' },
                  { id: 'comm', label: 'Commission Summary', path: 'generate-commission-summary', endpoint: 'center-accounts', color: 'bg-emerald-700 hover:bg-emerald-800 text-white' },
                  { id: 'bank', label: 'Bank Statement', path: 'generate-bank-statement', endpoint: 'center-accounts', color: 'bg-sky-700 hover:bg-sky-800 text-white' },
                ].map(r => (
                  <div key={r.id} className="flex flex-col gap-1.5">
                    <Button
                      className={r.color}
                      data-testid={`or-dl-${r.id}`}
                      onClick={() => fetchReportPdf(r, 'download')}
                    >
                      <Download className="w-4 h-4 mr-2" /> {r.label}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7"
                      data-testid={`or-preview-${r.id}`}
                      onClick={() => fetchReportPdf(r, 'preview')}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                    </Button>
                  </div>
                ))}
                {/* MG Report — uses a different endpoint (accepts from_month/to_month) */}
                <div className="flex flex-col gap-1.5">
                  <Button
                    className="bg-indigo-700 hover:bg-indigo-800 text-white"
                    data-testid="or-dl-mg"
                    onClick={() => fetchMgPdf('download')}
                  >
                    <Download className="w-4 h-4 mr-2" /> MG Report
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs h-7" data-testid="or-preview-mg" onClick={() => fetchMgPdf('preview')}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                  </Button>
                </div>
              </div>
          </CollapsibleSection>

          {/* Franchise Owner Ledger — separate from the Email Pack ZIP */}
          <CollapsibleSection
            id="or-owner-ledger-card"
            testId="or-owner-ledger-card"
            className="border-2 border-violet-200 bg-violet-50/30"
            title={<span className="flex items-center gap-2"><FileText className="w-4 h-4 text-violet-700" /> Franchise Owner Ledger</span>}
            description={<>HQ ↔ Franchise running account for {center} · {year}-{month}. Available here separately — not bundled into the Email Pack ZIP.</>}
            defaultOpen={true}
          >
            <div className="flex gap-3 flex-wrap">
                <Button className="bg-violet-700 hover:bg-violet-800 text-white" data-testid="or-owner-ledger-download" onClick={() => fetchOwnerLedgerPdf('download', 'month')}>
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button variant="outline" data-testid="or-owner-ledger-preview" onClick={() => fetchOwnerLedgerPdf('preview', 'month')}>
                  <Eye className="w-4 h-4 mr-2" /> View PDF
                </Button>
                <span className="text-xs text-muted-foreground self-center">Filter: selected month (use the global Year/Month above)</span>
              </div>
          </CollapsibleSection>

          {/* Sales / Expense Excel — month / date-range / single date */}
          <CollapsibleSection
            id="or-sales-expense-excel"
            testId="or-sales-expense-excel"
            className="border-2 border-emerald-200 bg-emerald-50/30"
            title={<span className="flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-emerald-700" /> Sales / Expense Excel</span>}
            description="Daily sales + expenses pulled live from the Sales Dashboard. Choose the period below."
            defaultOpen={true}
          >
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <Select value={seMode} onValueChange={setSeMode}>
                  <SelectTrigger className="w-40 h-9" data-testid="se-mode-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Full Month</SelectItem>
                    <SelectItem value="range">Date Range</SelectItem>
                    <SelectItem value="date">Specific Date</SelectItem>
                  </SelectContent>
                </Select>
                {seMode === 'month' && (
                  <span className="text-sm text-muted-foreground"><Calendar className="inline w-4 h-4 mr-1" />{year}-{month}</span>
                )}
                {seMode === 'range' && (
                  <>
                    <Input type="date" value={seStart} onChange={(e) => setSeStart(e.target.value)} className="w-40 h-9" data-testid="se-start" />
                    <span className="text-xs text-muted-foreground">to</span>
                    <Input type="date" value={seEnd} onChange={(e) => setSeEnd(e.target.value)} className="w-40 h-9" data-testid="se-end" />
                  </>
                )}
                {seMode === 'date' && (
                  <Input type="date" value={seStart} onChange={(e) => setSeStart(e.target.value)} className="w-40 h-9" data-testid="se-date" />
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="se-download" onClick={() => fetchSalesExpenseExcel('download')}>
                  <Download className="w-4 h-4 mr-2" /> Download Excel
                </Button>
                <Button variant="outline" data-testid="se-preview" onClick={() => fetchSalesExpenseExcel('preview')}>
                  <Eye className="w-4 h-4 mr-2" /> Open Excel
                </Button>
              </div>
            </div>
          </CollapsibleSection>

          {/* Raw Uploaded Files — Swiggy / Zomato / Bank Statement etc. */}
          <CollapsibleSection
            id="or-raw-files-card"
            testId="or-raw-files-card"
            className="border-2 border-sky-200 bg-sky-50/30"
            title={<span className="flex items-center gap-2"><FileBox className="w-4 h-4 text-sky-700" /> Raw Uploaded Files — {year}-{month}</span>}
            description="Original Excel/PDF files uploaded for commission upload + bank reconciliation. View-only."
            defaultOpen={false}
            headerExtra={rawFiles.length > 0 && <Badge variant="secondary">{rawFiles.length} file{rawFiles.length === 1 ? '' : 's'}</Badge>}
          >
            {rawFilesLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : rawFiles.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No raw files uploaded yet for this month. They will appear here automatically after Commission / Bank Reconciliation uploads.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-sky-100/60 text-left">
                        <th className="p-2">Type</th>
                        <th className="p-2">Filename</th>
                        <th className="p-2">Size</th>
                        <th className="p-2">Uploaded</th>
                        <th className="p-2">By</th>
                        <th className="p-2 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rawFiles.map((f) => (
                        <tr key={f.raw_id} className="border-t hover:bg-white">
                          <td className="p-2">
                            <Badge variant="secondary" className="capitalize">
                              {f.kind === 'commission' ? (f.platform || 'commission') : f.kind?.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="p-2 font-medium">{f.original_filename}</td>
                          <td className="p-2 text-muted-foreground">{f.size_kb} KB</td>
                          <td className="p-2 text-xs text-muted-foreground">{(f.uploaded_at || '').slice(0, 10)}</td>
                          <td className="p-2 text-xs text-muted-foreground">{f.uploaded_by}</td>
                          <td className="p-2 text-right">
                            <Button size="sm" variant="outline" data-testid={`raw-download-${f.raw_id}`}
                                    onClick={() => downloadRawFile(f.raw_id, f.original_filename)}>
                              <Download className="w-3.5 h-3.5 mr-1" /> Download
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </CollapsibleSection>

          {/* KPI cards, Sales Breakdown, Platform Commissions and Expense
              Breakdown were moved to the Overview sub-tab above; the Reports
              sub-tab shows only downloadable artefacts. */}
        </>
      )}

          </TabsContent>

          {/* ── LEDGERS SUB-TAB — same UI as Center Accounts ─────────────── */}
          <TabsContent value="ledgers" className="space-y-4" data-testid="or-monthly-ledgers-content">
            {center && (canDownload || canBypassRelease) ? (
              <LedgersTab
                session={session}
                selectedCenter={center}
                country={report?.country}
                readOnly={!canBypassRelease}
              />
            ) : (
              <Card><CardContent className="p-6 text-center text-slate-500">
                Ledgers become available once this month is released.
              </CardContent></Card>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Inline Preview modal */}
      </TabsContent>

        <TabsContent value="smart" className="space-y-4">
          <SmartCenterView
            session={session}
            center={center}
            month={`${year}-${month}`}
            currency={String(center || '').toUpperCase().endsWith('-PERTH') ? 'AUD' : 'INR'}
            onOpenMonthly={openMonthlySection}
          />
        </TabsContent>

        <TabsContent value="health" className="space-y-4" data-testid="or-health-tab-content">
          <CenterHealth
            embedded
            externalCenter={center}
            externalMonth={`${year}-${month}`}
          />
        </TabsContent>

        <TabsContent value="expense-intel" className="space-y-4" data-testid="or-expense-intel-tab-content">
          {!center ? (
            <Card><CardContent className="p-6 text-center text-slate-500">Select a center above to view Expense Intelligence.</CardContent></Card>
          ) : (
            <ExpenseIntelligenceTab
              session={session}
              center={center}
              month={`${year}-${month}`}
              currency={String(center || '').toUpperCase().endsWith('-PERTH') ? 'AUD' : 'INR'}
              editable={false}
              simplified={true}
              storageKey="owner-ei"
            />
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!previewBlobUrl} onOpenChange={(o) => { if (!o) { if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl); setPreviewBlobUrl(null); } }}>
        <DialogContent className="max-w-5xl h-[85vh] p-0 flex flex-col" data-testid="or-preview-modal">
          <DialogHeader className="px-5 py-3 border-b border-slate-200">
            <DialogTitle className="text-base">{previewTitle || 'Preview'}</DialogTitle>
            <DialogDescription className="text-xs">View the report inline before downloading.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {previewBlobUrl && (
              <iframe title="report-preview" src={previewBlobUrl} className="w-full h-full" data-testid="or-preview-frame" />
            )}
          </div>
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
            <Button variant="outline" size="sm" onClick={() => { if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl); setPreviewBlobUrl(null); }} data-testid="or-preview-close">Close</Button>
            {previewBlobUrl && (
              <a href={previewBlobUrl} download={`${previewTitle.replace(/[^a-z0-9]+/gi, '_')}.pdf`} data-testid="or-preview-download">
                <Button size="sm" className="bg-[#8B0000] hover:bg-[#6B0000] text-white">
                  <Download className="w-4 h-4 mr-1" /> Download PDF
                </Button>
              </a>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
